import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getGenericWhatsAppLink } from '../../utils/whatsapp';

// Official WhatsApp SVG Icon (chat bubble with phone)
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const FinalCTA = () => {
  const { language, isEnglish } = useLanguage();
  const { t } = useTranslation();
  const whatsappLink = getGenericWhatsAppLink(language);

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-white to-teal-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100 overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-100/50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative text-center">
            {/* Portrait Photo */}
            <div className="w-24 h-24 md:w-28 md:h-28 mx-auto mb-6 rounded-full overflow-hidden shadow-xl ring-4 ring-teal-100">
              <img
                src="https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,w_200,h_200,c_fill,g_face/v1767553321/moi_vakvo1.jpg"
                alt={isEnglish ? "Your contact in Hurghada" : "Votre contact à Hurghada"}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            {/* Text */}
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">
              {isEnglish ? 'Have a question or doubt?' : 'Une question ou un doute ?'}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto">
              {isEnglish 
                ? 'Write to me directly on WhatsApp, I respond quickly.'
                : 'Écrivez-moi directement sur WhatsApp, je vous réponds en français.'}
            </p>

            {/* CTA Button */}
            <Button
              asChild
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-8 py-6 rounded-full shadow-xl shadow-emerald-200 transition-all duration-300 hover:shadow-emerald-300 hover:scale-105 group"
            >
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <WhatsAppIcon className="w-5 h-5 mr-2" />
                {t('finalCta.cta')}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>

            <p className="text-sm text-slate-400 mt-4">
              {isEnglish ? 'Fast response guaranteed • No commitment' : 'Réponse rapide garantie • Aucun engagement'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
